/**
 * PublicShareViewer.tsx - Public view of a shared chat, in the app shell.
 *
 * Layout: brand header (sidebar toggle + logo + circle + CTA) · a simple left
 * sidebar (New Chat + "no chat yet") · the shared conversation READ-ONLY in the
 * main panel. The share link is public (GET /ua/chat/share/:token, no auth).
 * "New Chat" / the CTA forward into the live app (/aichat/<circle>/new), which
 * logs the visitor in if needed and lands them in the shared chat's circle.
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Box, Button, Card, HStack, Spinner, Text, VStack, Link, Accordion, For,
    Heading, IconButton, useBreakpointValue,
} from '@chakra-ui/react'
import { useApi, bookCairReaderUrl } from 'core'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import { LuPanelLeftClose, LuPanelLeftOpen, LuPlus } from 'react-icons/lu'
import { SmartProse } from '@/ui/components/ui/SmartProse'
import { SharedAudioPlayer } from '@/ui/components/SharedAudioPlayer'
import { sanitizeMalformedLinks } from '@/utilities/senitizeMalformedLinks'
import { formatDate } from '@/utilities/formatDate'
import { VectorSearchChunk } from '@/types/post.types'
import { AvatarImage } from '@/ui/components/AvatarImage'
import { getAvatarUrl } from '@/utilities/imageUtils'
import { brandLogoSrc } from '@/utilities/brandLogo'
import { InstallBar, InstallHeaderButton, SidebarInstallEntry, useAppInstall } from '@/ui/components/AppInstall'
import { canShowAppInstall, dismissInstallBar, isInstallBarDismissed } from '@/utilities/appInstall'
import iqraSVG from '/iqra.svg'

interface ShareMessage {
    _id: string
    tags?: string[]
    content?: { nsHtml?: string }
    vector_search_results?: VectorSearchChunk[]
    created?: string
    audio?: { audioUrl: string; duration?: number; format?: string }
}

interface ShareCircle { _id: string; name?: string; image?: string }
interface ShareData { messages: ShareMessage[]; circle: ShareCircle | null }

// The reader lives in the main react-app. On localhost it runs on :3000;
// in prod it shares this origin. Mirrors VectorSearchResult's link building.
const appOrigin =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'http://localhost:3000'
        : typeof window !== 'undefined'
            ? `https://${window.location.host}`
            : ''

const sourceHref = (chunk: VectorSearchChunk): string => {
    const { postId, chunkId } = chunk.metadata || ({} as any)
    if (!postId) return '#'
    const hash = `#?chunk=${postId}${chunkId ? `-${chunkId}` : ''}`
    return `${appOrigin}${bookCairReaderUrl(postId)}${hash}`
}

// The share endpoint sends the BOOK's title plus a 1-based `pageNumber` for a source
// that is one page of a book. Every page of a book shares that single title, so without
// the number a hit spanning several pages lists the same name over and over.
const sourceLabel = (chunk: VectorSearchChunk, index: number, t: (key: string) => string): string => {
    const { title, pageNumber } = chunk.metadata || ({} as any)
    if (title && pageNumber != null) {
        return `${title} - ${t('page')} ${pageNumber}`
    }
    return title || `${t('source') || 'Source'} ${index + 1}`
}

const Sources: React.FC<{ sources: VectorSearchChunk[]; isRTL: boolean }> = ({ sources, isRTL }) => {
    const { t } = useTranslation()
    return (
        <Box width="100%" marginBottom="3" borderRadius="var(--cair-radius-md)" background="var(--cair-surface)" overflow="hidden">
            <Accordion.Root width="100%" collapsible>
                <Accordion.Item value="sources" border="0">
                    <Accordion.ItemTrigger width="100%" justifyContent="space-between" cursor="pointer" paddingX="4" paddingY="3" flexDirection={isRTL ? 'row-reverse' : 'row'}>
                        <HStack gap="2" flexDirection={isRTL ? 'row-reverse' : 'row'}>
                            <img src={iqraSVG} />
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">{t('sources') || 'Sources'} ({sources.length})</Text>
                        </HStack>
                        <Accordion.ItemIndicator />
                    </Accordion.ItemTrigger>
                    <Accordion.ItemContent paddingX="4" paddingBottom="3" maxHeight="300px" overflowY="auto">
                        <VStack gap={2} marginTop="2" align="stretch">
                            <For each={sources}>
                                {(chunk, index) => (
                                    <Link key={`${chunk.metadata?.postId}-${index}`} href={sourceHref(chunk)} target="_blank" rel="noopener noreferrer" width="100%" _hover={{ textDecoration: 'none' }}>
                                        <Card.Root variant="outline" width="100%" border="0" borderRadius="8px" background="rgba(255,255,255,0.95)">
                                            <Card.Body paddingY="2" paddingX="3">
                                                <Text dir="auto" fontSize="xs" color="gray.700" fontWeight="medium" truncate>{sourceLabel(chunk, index, t)}</Text>
                                            </Card.Body>
                                        </Card.Root>
                                    </Link>
                                )}
                            </For>
                        </VStack>
                    </Accordion.ItemContent>
                </Accordion.Item>
            </Accordion.Root>
        </Box>
    )
}

const Message: React.FC<{ message: ShareMessage; isRTL: boolean }> = ({ message, isRTL }) => {
    const isAI = !!message.tags?.includes('ai generated')
    // Shared chats are public: sanitize the stored HTML before injecting it,
    // since the audience is untrusted (a malicious chat author must not be able
    // to run script in a viewer's browser).
    const html = DOMPurify.sanitize(sanitizeMalformedLinks(message.content?.nsHtml || ''))

    if (!isAI) {
        return (
            <HStack justify={isRTL ? 'start' : 'end'} padding={{ base: '12px 16px', md: 4 }} width="100%">
                <Card.Root maxWidth={{ base: '100%', md: '90%' }} background="var(--cair-premium-subtle)" borderRadius="var(--cair-radius-md)" border="0">
                    <Card.Body gap="1" paddingY="2">
                        {message.created && <Text fontSize="xs" color="gray.500" textAlign="end">{formatDate(message.created)}</Text>}
                        <Box className="chakra-card__description" css={{ color: 'fg.muted' }}>
                            <SmartProse htmlContent={html} dir="auto" maxWidth="unset"><div dangerouslySetInnerHTML={{ __html: html }} /></SmartProse>
                        </Box>
                    </Card.Body>
                </Card.Root>
            </HStack>
        )
    }

    return (
        <HStack padding={{ base: '12px 16px', md: 4 }} width="100%" align="start">
            <Card.Root width="100%" maxWidth={{ base: '100%', md: '90%' }} variant="outline" background="transparent" border="0">
                <Card.Body gap="2">
                    {message.vector_search_results && message.vector_search_results.length > 0 && (
                        <Sources sources={message.vector_search_results} isRTL={isRTL} />
                    )}
                    {/* Audio at the top of the post, directly under the sources bar. */}
                    {message.audio?.audioUrl && (
                        <SharedAudioPlayer audioUrl={message.audio.audioUrl} duration={message.audio.duration} />
                    )}
                    <Box className="chakra-card__description" css={{ color: 'fg.muted' }}>
                        <SmartProse htmlContent={html} dir="auto" maxWidth="unset"><div dangerouslySetInnerHTML={{ __html: html }} /></SmartProse>
                    </Box>
                </Card.Body>
            </Card.Root>
        </HStack>
    )
}

const SharedConversation: React.FC<{ messages: ShareMessage[]; isRTL: boolean }> = ({ messages, isRTL }) => (
    <Box flex="1" overflowY="auto" overflowX="hidden" width="100%">
        <VStack width="full" align="stretch" gap={0} maxWidth="1080px" marginX="auto" paddingY={4}>
            <For each={messages}>{(m) => <Message key={m._id} message={m} isRTL={isRTL} />}</For>
        </VStack>
    </Box>
)

// Simple sidebar: New Chat at the top, empty "no chat yet" below.
const SimpleSidebar: React.FC<{ onNewChat: () => void; onDownloadApp?: () => void }> = ({ onNewChat, onDownloadApp }) => {
    const { t, i18n } = useTranslation()
    return (
        <Box className="p-2" height="100%" display="flex" flexDirection="column" background="var(--cair-surface)" border="1px solid var(--cair-divider)">
            <Box padding={2} marginBottom={2} width="100%">
                <HStack justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                    <HStack gap={2} flex="1" minWidth={0}>
                        <img src={brandLogoSrc()} alt="" height={20} style={{ height: 20, width: 'auto' }} />
                        <Heading size="md" truncate textAlign={i18n.dir() === 'rtl' ? 'right' : 'left'}>{t('chats') || 'Chats'}</Heading>
                    </HStack>
                    <Button size="xs" background="var(--cair-accent)" color="white" borderRadius="var(--cair-radius-lg)" _hover={{ background: 'var(--cair-accent-hover)' }} flexShrink={0} onClick={onNewChat}>
                        <LuPlus />{t('new_chat') || 'New chat'}
                    </Button>
                </HStack>
            </Box>
            <Box paddingX={3} paddingY={4} flex="1">
                <Text dir="auto" color="fg.muted" fontSize="sm" textAlign="center">{t('no_chat_yet') || 'No chat yet'}</Text>
            </Box>
            {onDownloadApp && (
                <Box padding={2} borderTop="1px solid var(--cair-divider)">
                    <SidebarInstallEntry onClick={onDownloadApp} />
                </Box>
            )}
        </Box>
    )
}

export const PublicShareViewer: React.FC = () => {
    const { shareToken } = useParams()
    const api = useApi()
    const navigate = useNavigate()
    const { t, i18n } = useTranslation()
    const isRTL = i18n.dir() === 'rtl'
    const isMobile = useBreakpointValue({ base: true, md: false })

    const [data, setData] = useState<ShareData | null>(null)
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const hostAllowsInstall = canShowAppInstall(window.location.hostname)
    const [instance, setInstance] = useState<{ instanceName?: string; domainName?: string }>()
    const showInstall = canShowAppInstall(window.location.hostname, instance)
    const [installBarDismissed, setInstallBarDismissed] = useState(isInstallBarDismissed)
    const { openInstall, overlays: installOverlays } = useAppInstall(!!isMobile)

    useEffect(() => { if (isMobile) setSidebarOpen(false) }, [isMobile])

    // A hostname without "mushtarek" may still be a Mushtarek deployment: ask the
    // public /ua/info, once the conversation is showing. A failure just keeps it hidden.
    useEffect(() => {
        if (status !== 'ready' || hostAllowsInstall) return
        let cancelled = false
        api.loadInstanceInfo().then(info => { if (!cancelled) setInstance(info) }).catch(() => {})
        return () => { cancelled = true }
    }, [status, hostAllowsInstall, api])

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            try {
                const result = await api.get({ path: `chat/share/${shareToken}` })
                if (cancelled) return
                setData({ messages: result.messages || [], circle: result.circle || null })
                setStatus('ready')
            } catch {
                if (!cancelled) setStatus('error')
            }
        }
        if (shareToken) load()
        return () => { cancelled = true }
    }, [shareToken, api])

    if (status === 'loading') {
        return <HStack justify="center" align="center" height="100dvh" background="white"><Spinner /></HStack>
    }
    if (status === 'error' || !data) {
        return (
            <VStack justify="center" align="center" height="100dvh" gap={2} background="white">
                <Text fontSize="md" fontWeight="medium">{t('share_unavailable') || 'This shared conversation is no longer available.'}</Text>
            </VStack>
        )
    }

    const circle = data.circle
    // Forward into the live app; the normal route handles login if needed.
    const onContinue = () => { if (circle?._id) navigate(`/aichat/${circle._id}/new`) }

    return (
        <HStack alignItems="stretch" width="100%" maxWidth="100vw" height="100dvh" gap={0} position="relative" boxSizing="border-box" overflow="hidden" dir={i18n.dir()}>
            {isMobile && sidebarOpen && (
                <Box position="fixed" inset="0" background="rgba(0,0,0,0.5)" zIndex="98" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Sidebar */}
            <Box
                width={sidebarOpen ? '260px' : '0'}
                height="100dvh"
                overflow="hidden"
                transition="width 0.3s ease-in-out, transform 0.3s ease-in-out"
                display="flex"
                flexDirection="column"
                position={{ base: 'fixed', md: 'relative' }}
                zIndex={{ base: '99', md: 'auto' }}
                left={isRTL ? 'auto' : '0'}
                right={isRTL ? '0' : 'auto'}
                top="0"
                transform={{ base: sidebarOpen ? 'translateX(0)' : isRTL ? 'translateX(100%)' : 'translateX(-100%)', md: 'translateX(0)' }}
            >
                <Box opacity={sidebarOpen ? 1 : 0} transition="opacity 0.2s ease-in-out" height="100%" overflowY="auto">
                    <SimpleSidebar onNewChat={onContinue} onDownloadApp={showInstall && isMobile ? openInstall : undefined} />
                </Box>
            </Box>

            {/* Main area */}
            <Box flex="1" display="flex" flexDirection="column" height="100dvh" overflow="hidden" width="100%" maxWidth="100vw" position="relative" boxSizing="border-box" background="white">
                {/* Branded header */}
                <HStack padding={2} height="var(--header-height)" position="sticky" top="0" zIndex="10" background="var(--cair-surface-elevated)" borderBottom="1px solid var(--cair-border)" boxShadow="var(--cair-shadow-sm)" gap={2}>
                    <IconButton aria-label="Toggle Sidebar" variant="ghost" color="#333333" _hover={{ background: 'var(--cair-accent-subtle)' }} onClick={() => setSidebarOpen(!sidebarOpen)}>
                        {sidebarOpen ? <LuPanelLeftClose /> : <LuPanelLeftOpen />}
                    </IconButton>
                    {circle && <AvatarImage size="sm" avatarFallbackName={circle.name || ''} avatarImageUrl={getAvatarUrl(circle.image)} />}
                    <VStack align={isRTL ? 'end' : 'start'} gap={0} minWidth={0} overflow="hidden">
                        <Text fontSize="sm" fontWeight="semibold" truncate maxWidth="100%">{circle?.name || t('shared_conversation') || 'Shared conversation'}</Text>
                        <Text fontSize="xs" color="gray.500">{t('shared_conversation') || 'Shared conversation'}</Text>
                    </VStack>
                    <Box flex="1" />
                    {showInstall && isMobile === false && <InstallHeaderButton onClick={openInstall} />}
                    {/* Temporarily hidden — flip `false` to re-enable the jump-into-app CTA */}
                    {false && (
                        <Button size="sm" flexShrink={0} onClick={onContinue}>
                            {t('continue_conversation') || 'Continue this conversation'}
                        </Button>
                    )}
                </HStack>

                <Box flex="1" overflow="hidden" position="relative" width="100%" display="flex" flexDirection="column">
                    <SharedConversation messages={data.messages} isRTL={isRTL} />
                </Box>

                {showInstall && isMobile && !installBarDismissed && (
                    <InstallBar onInstall={openInstall} onDismiss={() => { dismissInstallBar(); setInstallBarDismissed(true) }} />
                )}
            </Box>
            {showInstall && installOverlays}
        </HStack>
    )
}

export default PublicShareViewer
